import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import PermissionRoute from './auth/PermissionRoute';
import GlobalLoader from './components/GlobalLoader';
import InitialPasswordDialog from './components/InitialPasswordDialog';
import AppShell from './components/AppShell';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useGpsTracking } from './hooks/useGpsTracking';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const FirmaRemotaPage = lazy(() => import('./pages/FirmaRemotaPage'));
const MiCuentaPage = lazy(() => import('./pages/MiCuentaPage'));
const MisCredencialesPage = lazy(() => import('./pages/MisCredencialesPage'));
const VisitasPage = lazy(() => import('./pages/VisitasPage'));
const VisitaEditPage = lazy(() => import('./pages/VisitaEditPage'));
const InformesPage = lazy(() => import('./pages/InformesPage'));
const ObservacionesCorrectivasPage = lazy(() => import('./pages/ObservacionesCorrectivasPage'));
const SolicitudesVisitaPage = lazy(() => import('./pages/SolicitudesVisitaPage'));
const ProcesadoTallerPage = lazy(() => import('./pages/ProcesadoTallerPage'));
const ActivosTecnicosPage = lazy(() => import('./pages/ActivosTecnicosPage'));
const TallerDashboardPage = lazy(() => import('./pages/TallerDashboardPage'));
const NativePlaygroundPage = lazy(() => import('./pages/NativePlaygroundPage'));
const MisFichadosPage = lazy(() => import('./pages/MisFichadosPage'));
const ConsumoDatosPage = lazy(() => import('./pages/ConsumoDatosPage'));
const MiAsistenciaPage = lazy(() => import('./pages/MiAsistenciaPage'));
const SoportePage = lazy(() => import('./pages/SoportePage'));
const ManualUsuarioPage = lazy(() => import('./pages/ManualUsuarioPage'));

function RouteLoader() {
    return <GlobalLoader message="Cargando modulo..." minHeight="45vh" />;
}

function App() {
    const { status, user } = useAuth();
    const { pathname } = useLocation();
    usePushNotifications();
    useGpsTracking();

    const mustChangePassword = status === 'authenticated' && Boolean(user?.must_change_password);
    let content = null;

    if (mustChangePassword) {
        content = (
            <Suspense fallback={<RouteLoader />}>
                <InitialPasswordDialog />
            </Suspense>
        );
    } else if (status !== 'loading') {
        content = (
        <Suspense fallback={<RouteLoader />}>
            <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route 
                        path="/playground" 
                        element={
                            <PermissionRoute permission="access.empresas">
                                <NativePlaygroundPage />
                            </PermissionRoute>
                        } 
                    />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/sistema/consumo-datos" element={<PermissionRoute internalOnly allPermissions={['access.telemetria_datos', 'telemetria_datos.view_any']}><ConsumoDatosPage /></PermissionRoute>} />
                    <Route path="/mis-fichados" element={<PermissionRoute internalOnly allPermissions={['access.jornadas']} anyPermissions={['jornadas.ver_propias', 'jornadas.ver_equipo']}><MisFichadosPage /></PermissionRoute>} />
                    <Route path="/mi-asistencia" element={<PermissionRoute internalOnly allPermissions={['access.asistencias']} anyPermissions={['asistencias.ver_propias', 'asistencias.ver_equipo', 'asistencias.informar_propia', 'asistencias.registrar_equipo', 'asistencias.ver_indicadores']}><MiAsistenciaPage /></PermissionRoute>} />
                    <Route path="/mi-asistencia/informar" element={<PermissionRoute internalOnly allPermissions={['access.asistencias', 'asistencias.informar_propia']}><MiAsistenciaPage report /></PermissionRoute>} />
                    <Route path="/soporte" element={<PermissionRoute allPermissions={['access.reportes_error', 'reportes_error.reportar_propio']}><SoportePage /></PermissionRoute>} />
                    <Route path="/manual-de-usuario" element={<PermissionRoute allPermissions={['access.manual_usuario', 'manual_usuario.view']}><ManualUsuarioPage /></PermissionRoute>} />
                    <Route path="/firma-remota/:token" element={<FirmaRemotaPage />} />
                    <Route
                        path="/dashboard"
                        element={
                            <PermissionRoute permission="access.dashboard">
                                <DashboardPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/mi-cuenta"
                        element={
                            <PermissionRoute permission="access.cuentas">
                                <MiCuentaPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/mis-credenciales"
                        element={
                            <PermissionRoute permissions={['credenciales.view_propias', 'access.credenciales']}>
                                <MisCredencialesPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/visitas"
                        element={
                            <PermissionRoute permission="access.visitas">
                                <VisitasPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/visitas/:visitaId/editar"
                        element={
                            <PermissionRoute permission="access.visitas">
                                <VisitaEditPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/informes"
                        element={
                            <PermissionRoute permission="access.informes">
                                <InformesPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/observaciones-correctivas"
                        element={
                            <PermissionRoute permission="access.observaciones">
                                <ObservacionesCorrectivasPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/solicitudes-visita"
                        element={
                            <PermissionRoute permission="access.solicitudes">
                                <SolicitudesVisitaPage />
                            </PermissionRoute>
                        }
                    />
                    <Route
                        path="/activos-tecnicos"
                        element={<PermissionRoute permission="access.activos_tecnicos"><ActivosTecnicosPage /></PermissionRoute>}
                    />
                    <Route
                        path="/procesados-mangueras"
                        element={<PermissionRoute permission="access.logistica_mangueras"><ProcesadoTallerPage /></PermissionRoute>}
                    />
                    <Route
                        path="/procesado-taller"
                        element={<PermissionRoute permission="access.procesado_taller"><TallerDashboardPage /></PermissionRoute>}
                    />
                    <Route
                        path="*"
                        element={<Navigate to={status === 'authenticated' ? '/dashboard' : '/login'} replace />}
                    />
                </Routes>
        </Suspense>
        );
    }

    const publicPage = pathname === '/login' || pathname === '/privacy-policy' || pathname.startsWith('/firma-remota/');
    if (status === 'authenticated' && !mustChangePassword && !publicPage) return <AppShell key={`${user?.id}:${user?.empresa_id}`}>{content}</AppShell>;

    return (
        <>
            {content}
        </>
    );
}

export default App;
